import { nanoid } from '@scalar-org/entities/utility'

import type { ScalarConfigLegacy } from '@/legacy'
import type { GuideExplicit } from '@/legacy/legacy'
import { resolveHeader } from '@/legacy/resolvers/resolve-header'
import type {
  HeadLinks,
  HeadMeta,
  LoadableContent,
  NavigationConfig,
  PageNavigationEntry,
  ScalarConfig,
} from '@/schema'
import type { NavigationRecord } from '@/schema/navigation/groups'
import { schemaCdn } from '@/schema/schema'
import { createUrlSlug } from '@/transformers/helpers'

type ExtendedDef = {
  uid: string
  title: string
  urlSlug: string
}

export const legacyTo2_0_0 = (
  config: ScalarConfigLegacy,
): {
  upgradedConfig: ScalarConfig
  legacyTheme?: {
    path: string
    cssString: string
  }
  legacyFooter?: {
    path: string
    footerString: string
  }
  legacyScript?: {
    path: string
    scriptString: string
  }
} => {
  const routes: NavigationConfig['routes'] = {}
  const header: NavigationConfig['header'] = []

  /** Create Uid based maps that can be used to resolve the header order for conversions */
  const { referenceSlugs, referenceUids } = config.references.reduce<{
    referenceUids: Map<
      string,
      ScalarConfigLegacy['references'][number] & ExtendedDef
    >
    referenceSlugs: Map<
      string,
      ScalarConfigLegacy['references'][number] & ExtendedDef
    >
  }>(
    (acc, reference) => {
      const extendedRef = {
        ...reference,
        uid: nanoid(),
        title: reference.name,
        urlSlug: createUrlSlug(reference),
      }

      acc.referenceUids.set(extendedRef.uid, extendedRef)

      if (extendedRef.slug) {
        acc.referenceSlugs.set(extendedRef.slug, extendedRef)
      }
      return acc
    },
    { referenceUids: new Map(), referenceSlugs: new Map() },
  )

  const { guideSlugs, guideUids } = config.guides.reduce<{
    guideUids: Map<string, ScalarConfigLegacy['guides'][number] & ExtendedDef>
    guideSlugs: Map<string, ScalarConfigLegacy['guides'][number] & ExtendedDef>
  }>(
    (acc, guide) => {
      const extendedGuide = {
        ...guide,
        uid: nanoid(),
        title: guide.name,
        urlSlug: createUrlSlug(guide),
      }
      acc.guideUids.set(extendedGuide.uid, extendedGuide)

      if (extendedGuide.slug) {
        acc.guideSlugs.set(extendedGuide.slug, extendedGuide)
      }
      return acc
    },
    { guideUids: new Map(), guideSlugs: new Map() },
  )
  // ---------------------------------------------------------------------------

  /** Legacy header is used to determine the navigation order of the migrated config */
  const legacyHeader = resolveHeader({
    references: referenceSlugs,
    guides: guideSlugs,
    header: config.header,
  })

  /** Once we have populated the router we must create the legacy header format */
  legacyHeader.forEach((entry) => {
    // For each guide we convert the sidebar to the new navigation format and create a top level navigation group
    if (entry.type === 'guide') {
      const guide = guideUids.get(entry.uid)
      if (!guide) return

      header.push({
        type: 'link',
        title: guide.title,
        to: guide.urlSlug,
      })
    }

    // For each reference we assign an appropriate navigation entry to the top level
    if (entry.type === 'reference') {
      const reference = referenceUids.get(entry.uid)
      if (!reference) return

      header.push({
        type: 'link',
        title: reference.title,
        to: reference.urlSlug,
      })
    }

    if (entry.type === 'link') {
      header.push({
        type: 'link',
        title: entry.title,
        to: entry.link,
        newTab: false,
      })
    }

    if (entry.type === 'group') {
      header.push({
        type: 'group',
        title: entry.title,
        children: entry.children
          .map((child) => {
            if (child.type === 'link') {
              return {
                type: 'link' as const,
                title: child.title,
                to: child.link,
              }
            }

            if (child.type === 'reference') {
              const reference = referenceUids.get(child.uid)
              if (!reference) return null

              return {
                type: 'link' as const,
                title: child.title,
                to: reference.urlSlug,
              }
            }

            if (child.type === 'guide') {
              const guide = guideUids.get(child.uid)
              if (!guide) return null

              return {
                type: 'link' as const,
                title: guide.title,
                to: guide.urlSlug,
              }
            }

            return null
          })
          .filter((e) => e !== null),
      })
    }
  })

  // Each guide gets mapped to its slugified name in the router
  for (const [_, guide] of guideUids) {
    if (!('sidebar' in guide)) continue

    routes[guide.urlSlug] = {
      title: guide.title,
      type: 'group',
      children: convertGuideSidebar(guide.sidebar),
    }

    if (!header.some((h) => 'path' in h && h.path === guide.urlSlug)) {
      header.push({
        type: 'link',
        title: guide.title,
        to: guide.urlSlug,
      })
    }
  }

  // Each reference gets mapped to its slugified name in the router
  for (const [_, reference] of referenceUids) {
    if ('path' in reference) {
      routes[reference.urlSlug] = {
        title: reference.title,
        type: 'openapi',
        filepath: reference.path,
        config: {},
      }
    }

    if ('namespace' in reference) {
      routes[reference.urlSlug] = {
        title: reference.title,
        type: 'openapi',
        namespace: reference.namespace,
        slug: reference.slug,
        version: reference.versions?.at(0) ?? 'latest',
        config: {},
      }
    }

    if ('liveLink' in reference) {
      routes[reference.urlSlug] = {
        title: reference.title,
        type: 'openapi',
        url: reference.liveLink,
        config: {},
      }
    }

    if (!header.some((h) => 'path' in h && h.path === reference.urlSlug)) {
      header.push({
        type: 'link',
        title: reference.title,
        to: reference.urlSlug,
      })
    }
  }

  const cssStyles =
    config.siteConfig.cssFiles?.map((path) => ({
      path,
    })) ?? []

  const legacyThemePath = 'theme.css'

  if (config.siteConfig.cssString) {
    cssStyles.push({
      path: legacyThemePath,
    })
  }

  const legacyFooterPath = 'footer.html'

  const meta: HeadMeta[] = []

  if (config.siteMeta.description) {
    meta.push({
      name: 'description',
      content: config.siteMeta.description,
    })
  }

  if (config.siteMeta.ogImage) {
    meta.push({
      property: 'og:image',
      content: config.siteMeta.ogImage,
    })
  }

  const links: HeadLinks[] = []

  if (config.siteMeta.favicon) {
    links.push({
      rel: 'icon',
      type: 'image/x-icon',
      href: config.siteMeta.favicon,
    })
  }

  const isValidTheme =
    config.siteConfig.theme && config.siteConfig.theme !== 'none'

  const scripts: LoadableContent[] = []

  const legacyScriptPath = 'head.js'

  if (config.siteConfig.headScript) {
    scripts.push({
      path: legacyScriptPath,
    })
  }

  return {
    upgradedConfig: {
      scalar: '2.0.0',
      root: config.root,
      $schema: schemaCdn,
      publishOnMerge: config.publishOnMerge,
      assetsDir: config.assetsDir,
      insertPageTitles: config.insertPageTitles,
      info: {
        title: config.siteMeta.title,
        description: config.siteMeta.description,
      },
      siteConfig: {
        customDomain: config.customDomain,
        subdomain: config.subdomain,
        loginPortal: config.loginPortal,
        ...(config.siteConfig.footer && {
          footer: {
            filepath: legacyFooterPath,
            belowSidebar: config.siteConfig.footerBelowSidebar,
          },
        }),
        routing: config.siteConfig.routing,
        head: {
          title: config.siteMeta.title,
          links,
          meta,
          styles: [...(cssStyles ? cssStyles : [])],
          scripts,
        },
        ...(isValidTheme && {
          theme: config.siteConfig.theme,
        }),
        logo: config.siteConfig.logo,
      },
      versions: {
        'default': {
          header,
          routes,
        },
      },
    },
    ...(config.siteConfig.cssString && {
      legacyTheme: {
        path: legacyThemePath,
        cssString: config.siteConfig.cssString,
      },
    }),
    ...(config.siteConfig.footer && {
      legacyFooter: {
        path: legacyFooterPath,
        footerString: config.siteConfig.footer,
      },
    }),
    ...(config.siteConfig.headScript && {
      legacyScript: {
        path: legacyScriptPath,
        scriptString: config.siteConfig.headScript,
      },
    }),
  }
}

/** Converts a legacy sidebar config to the new navigation format */
function convertGuideSidebar(
  items: GuideExplicit['sidebar'],
): NavigationRecord {
  const navigation: NavigationRecord = {}

  // For each item we convert it to the navigation entry format
  items.forEach((item) => {
    const slug = createUrlSlug(item)
    if (!slug) return

    /** Folders get converted to groups with a nested navigation object */
    if (item.type === 'folder') {
      navigation[slug] = {
        title: item.name,
        type: 'group',
        mode: 'folder',
        children: convertGuideSidebar(item.children),
      }
    }

    if (item.type === 'page') {
      const pageEntry: PageNavigationEntry = {
        type: 'page',
        filepath: item.path,
        ...(item.name && {
          title: item.name,
        }),
      }

      // If a page has children we add a group with the page as the root
      if (item?.children) {
        navigation[slug] = {
          type: 'group',
          mode: 'folder',
          title: item.name ?? 'Page group',
          open: item.defaultOpen,
          icon: item.icon,
          children: {
            '/': pageEntry,
            ...convertGuideSidebar(item.children),
          },
        }
      } else {
        pageEntry.icon = item.icon // We only add the icon the primary entry
        navigation[slug] = pageEntry
      }
    }

    if (item.type === 'link') {
      navigation[slug] = {
        type: 'link',
        url: item.url,
        title: item.name,
        icon: item.icon,
      }
    }
  })

  return navigation
}
